import { Controller } from "@hotwired/stimulus"
import { autocomplete } from '@algolia/autocomplete-js'

const MIN_QUERY_LENGTH = 2
// How long a query has to sit with no results before it counts as one the
// visitor meant, not one they were still typing.
const EMPTY_QUERY_SETTLE_MS = 1000

// Connects to data-controller="public--learn-search"
//
// The help center's typeahead. Picking an article opens it; Enter opens the
// best match. Queries that find nothing are reported, so the articles agents
// look for and can't find are the next ones to write.
export default class extends Controller {
  static targets = ['input', 'status']
  static values = { url: String }

  connect() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return

    // The static field the server rendered for no-JS visitors.
    this.inputTarget.replaceChildren()

    this.autocompleteInstance = autocomplete({
      container: this.inputTarget,
      detachedMediaQuery: 'none',
      placeholder: 'Search the help articles',
      openOnFocus: false,
      classNames: { panel: 'public-learn-search-panel' },
      getInputProps: ({ props }) => ({
        ...props,
        'aria-labelledby': undefined,
        'aria-label': 'Search the help articles',
        'aria-describedby': this.statusTarget.id,
      }),
      translations: { submitButtonTitle: 'Search' },
      getSources: ({ query }) => this.getSource(query),
      onSubmit: ({ state }) => this.openBestMatch(state.query),
      onReset: () => {
        this.searchQuery = ''
        this.setStatus('Type what you want to do.')
      },
      navigator: {
        // A click reaches onSelect alone, so onSelect navigates and Enter
        // mustn't do it again.
        navigate: () => {},
      },
    })
  }

  disconnect() {
    clearTimeout(this.emptyQueryTimer)
    this.autocompleteInstance?.destroy()
    this.autocompleteInstance = null
  }

  async openBestMatch(query) {
    query = query.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      this.setStatus('Type two or more characters.')
      return
    }

    const results = await this.fetchArticles(query)
    if (query === this.searchQuery && results.length) {
      this.selectArticle(results[0], 0, 'submit')
    }
  }

  async fetchArticles(query) {
    this.searchQuery = query
    clearTimeout(this.emptyQueryTimer)

    const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`)
    if (!response.ok) throw new Error(`Help search failed (${response.status})`)

    const { results } = await response.json()
    if (query === this.searchQuery) {
      this.resultCount = results.length
      if (results.length) {
        this.setStatus(results.length === 1 ? '1 article found.' : `${results.length} articles found. Pick one.`)
      } else {
        this.setStatus(`No article matches “${query}”. Try another word for it.`)
        this.emptyQueryTimer = setTimeout(() => {
          if (query === this.searchQuery) window.posthog?.capture('www.learn_search_empty', { query })
        }, EMPTY_QUERY_SETTLE_MS)
      }
    }
    return results
  }

  getSource(query) {
    query = query.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      this.searchQuery = query
      clearTimeout(this.emptyQueryTimer)
      this.setStatus('Type two or more characters.')
      return []
    }

    return this.fetchArticles(query).then(results => [{
      sourceId: 'articles',
      getItems: () => results,
      getItemInputValue: ({ item }) => item.title,
      getItemUrl: ({ item }) => item.path,
      onSelect: ({ item }) => this.selectArticle(item, results.indexOf(item), 'select'),
      templates: {
        item: ({ item, html }) => html`
          <div class="aa-ItemWrapper">
            <div class="aa-ItemContent">
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitle">
                  ${item.title}
                </div>
                <div class="aa-ItemContentDescription">${item.section} · ${item.description}</div>
              </div>
            </div>
          </div>`,
      },
    }])
  }

  // The page changes right after, so the event goes by beacon.
  selectArticle(item, position, method) {
    window.posthog?.capture('www.learn_search_selected', {
      method,
      query: this.searchQuery,
      result_count: this.resultCount,
      position,
      path: item.path,
    }, { transport: 'sendBeacon' })
    Turbo.visit(item.path)
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }
}
